import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, StyleSheet } from 'react-native';
import { useRecoilValue } from 'recoil';
import { SECOND } from 'time-constants';

import { lowerDisplayLineTestIDs, Theme } from '@constants';
import { useShowNextLDLProduct } from '@hooks';
import { pollenIndexState } from '@store';

import { LDLPollenScale } from './LDLPollenScale';

import { TWCText } from '../../primitives/TWCText/TWCText';

const tPath = 'lowerDisplayLine.pollen.';
const testIDPath = lowerDisplayLineTestIDs.pollen;

const LDLPollenAPI = () => {
  const { data } = useRecoilValue(pollenIndexState);
  const showNextProduct = useShowNextLDLProduct();

  useEffect(() => {
    !data && showNextProduct(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (!data?.pollenForecast12hour || Object.keys(data.pollenForecast12hour).length === 0) {
    return <></>;
  }

  return <LDLPollenUI data={data} onFinish={showNextProduct} />;
};

const LDLPollenUI = ({
  data: {
    pollenForecast12hour: {
      daypartName,
      grassPollenCategory,
      grassPollenIndex,
      ragweedPollenCategory,
      ragweedPollenIndex,
      treePollenCategory,
      treePollenIndex,
    },
  },
  onFinish,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    const animation = Animated.timing(new Animated.Value(0), {
      duration: 5 * SECOND,
      useNativeDriver: true,
    });

    animation.start(({ finished }) => finished && onFinish?.());
    return animation.reset;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * If before 3pm, first indexed item in response is "Today" and Today's data is displayed
   * If after 3pm, it is "Tonight" and Tomorrow's data is displayed
   */
  const isToday = daypartName[0] === 'Today';

  /**
   * If before 3pm (Today), values are indexed at 0
   * If after 3pm (Tomorrow), values are indexed at 1
   */
  const relevantPollenValues = [
    {
      type: t(`${tPath}treeLabel`),
      category: isToday ? treePollenCategory[0] : treePollenCategory[1],
      index: isToday ? treePollenIndex[0] : treePollenIndex[1],
    },
    {
      type: t(`${tPath}grassLabel`),
      category: isToday ? grassPollenCategory[0] : grassPollenCategory[1],
      index: isToday ? grassPollenIndex[0] : grassPollenIndex[1],
    },
    {
      type: t(`${tPath}weedLabel`),
      category: isToday ? ragweedPollenCategory[0] : ragweedPollenCategory[1],
      index: isToday ? ragweedPollenIndex[0] : ragweedPollenIndex[1],
    },
  ];

  /**
   * Sort by index
   * If there is a tie, order of tree -> grass -> ragweed will be returned as first item due to order in array
   */
  const pollenTypeToDisplay = relevantPollenValues.sort((a, b) => b.index - a.index)[0];

  return (
    <>
      <TWCText style={styles.productText} testID={testIDPath.productLabel} variant={'condensedText36Transparent'}>
        {isToday ? t(`${tPath}productLabelToday`) : t(`${tPath}productLabelTomorrow`)}
      </TWCText>
      <TWCText style={styles.typeText} testID={testIDPath.pollenType} variant={'condensedText36'}>
        {pollenTypeToDisplay.type}
      </TWCText>
      <LDLPollenScale severity={pollenTypeToDisplay.index} />
      <TWCText style={styles.categoryText} testID={testIDPath.pollenCategory} variant={'condensedText36'}>
        {pollenTypeToDisplay.category}
      </TWCText>
    </>
  );
};

export const LDLPollen = {
  API: LDLPollenAPI,
  UI: LDLPollenUI,
};

const styles = StyleSheet.create({
  categoryText: {
    paddingLeft: Theme.px(18),
  },
  itemText: {
    paddingLeft: Theme.px(18),
    paddingRight: Theme.px(42),
  },
  pageWrapper: {
    flexDirection: 'row',
    position: 'absolute',
  },
  productText: {
    paddingRight: Theme.px(48),
  },
  typeText: {
    paddingRight: Theme.px(32),
  },
});
